/* Touch detection — runs immediately if body exists, otherwise defers to DOMContentLoaded */
(function setTouchClass() {
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', setTouchClass);
    return;
  }
  if (!document.body.classList.contains('ks-touchevents') && !document.body.classList.contains('ks-no-touchevents')) {
    if ('ontouchstart' in window) {
      document.body.classList.add('ks-touchevents');
    } else {
      document.body.classList.add('ks-no-touchevents');
    }
  }
})();

if ( typeof debounce !== 'function' ) {
  window.debounce = (fn, wait) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

window.KS_PREFIX = 'ks-';

window.KS = window.KS || {};
window.KS.settings = window.KS.settings || {};

/* Layout ceiling, read from the KSS stylesheet (--ks-theme-max-width) rather than a
   setting. The embed already sets it in <head>; this is the fallback for a producer that
   did not, and for a stylesheet that arrived later. 1280 is hydra's schema default. */
if (!window.KS.settings.theme_max_width) {
  window.KS.settings.theme_max_width = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--ks-theme-max-width')) || 1280;
}
window.KS.previouslyFocused = null;
window.KS.keycodes = {
  TAB: 9,
  ESC: 27,
  DOWN: 40,
  RIGHT: 39,
  UP: 38,
  LEFT: 37,
  RETURN: 13
};
window.KS.functions = (() => {

  /* ---------------- IMAGE HELPERS ---------------- */

  const setImagesize = (img) => {
    let size = 0;
    if (img.offsetWidth / img.dataset.ratio < img.offsetHeight) {
      size = Math.ceil(img.offsetHeight * parseFloat(img.dataset.ratio));
    } else {
      size = Math.ceil(img.offsetWidth);
    }
    if ( size > 0 ) {
      img.setAttribute('sizes', `${size}px`);
    }
  };

  // Port of hydra assets/theme.js (4c05421, "Image sizes observer"): observed images are
  // tracked so a node that left the DOM (a re-rendered gallery slide, a filtered grid) is
  // unobserved instead of leaking, and `observeImages(root)` re-attaches the observer to
  // every `img[data-ratio]` under a root — section-main-product.js calls it on the slider's
  // ready/reset/destroy, and the collection grid dispatches the filtered event below.
  const observedImages = new Set();

  const resizeObserver = ('ResizeObserver' in window)
    ? new ResizeObserver(entries => {
        for (const entry of entries) {
          if ( ! entry.target.isConnected ) {
            resizeObserver.unobserve(entry.target);
            observedImages.delete(entry.target);
            continue;
          }
          setImagesize(entry.target);
        }
      })
    : null;

  const handleImageLoad = img => {
    if ( !img ) return;
    setImagesize(img);
    resizeObserver?.observe(img);
    observedImages.add(img);
  };

  const observeImages = (root = document) => {
    for (const img of observedImages) {
      if ( ! img.isConnected ) {
        resizeObserver?.unobserve(img);
        observedImages.delete(img);
      }
    }
    root?.querySelectorAll('img[data-ratio]').forEach(handleImageLoad);
  };

  document.addEventListener('krown:main-collection-grid:filtered', () => {
    observeImages(document.getElementById('CollectionProductGrid'));
  });

  /* ---------------- MONEY ---------------- */

  // Port of _framework/js/js_framework/component-money.js, which hydra loads ungated from
  // the layout and the manifest excludes from the payload (`assets.excludeFrameworkJs`): the
  // app has no layout, so the formatter lives in the entry script instead. Consumer today:
  // component-quantity-selector.js, for the add-to-cart button price that follows the
  // quantity (`update_price_on_quantity`), read as
  // KS.functions.formatMoney(cents, KS.settings.shop_money_format) + KS.settings.iso_code.
  const formatMoney = (cents, format) => {

    if ( typeof cents === 'string' ) {
      cents = cents.replace('.', '');
    }

    let value = '';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const formatString = format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      thousands = thousands || ',';
      decimal = decimal || '.';

      if ( isNaN(number) || number === null ) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      const parts = number.split('.');
      const dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch ( (formatString.match(placeholderRegex) || [])[1] ) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      case 'amount_no_decimals_with_space_separator':
        value = formatWithDelimiters(cents, 0, ' ');
        break;
      case 'amount_with_apostrophe_separator':
        value = formatWithDelimiters(cents, 2, "'");
        break;
    }

    return formatString.replace(placeholderRegex, value);

  };

  /* ---------------- SCROLL PROGRESS CORE ---------------- */

  const progressElements = new Set();

  let ticking = false;
  let needsHeightSync = false;

  const updateScrollProgress = () => {
    
    if (needsHeightSync) {
      parallaxHeightPairs.forEach(({ target, ref }) => {
        target.style.height = `${ref.offsetHeight}px`;
      });
      needsHeightSync = false;
    }

    const viewportHeight = window.innerHeight;
  
    progressElements.forEach(({ el, callback, mode }) => {
      const rect = el.getBoundingClientRect();
      let progress = 0;
  
      if ( mode === 'enter' ) {
        // 0% when section top is at viewport bottom
        // 100% when section top is at viewport top
        // exception when the section is at the bottom of the page
        const vh = innerHeight;
        const max = document.documentElement.scrollHeight - vh;
        const top = rect.top + scrollY;
        const start = top - vh;
        progress = ((scrollY - start) * 100) / (Math.min(top, max) - start);
      } else if ( mode === 'cover' ) {
        // 0% when section is fully visible at top
        // 100% when section has fully scrolled past (bottom <= 0)
        const start = 0;
        const end = -(rect.height - viewportHeight);
        const scrolled = Math.min(Math.max(rect.top, end), start);
        progress = ((start - scrolled) * 100) / (start - end);
      } else {
        // default: 0% when section enters viewport,
        // 100% when fully scrolled through
        progress = ((viewportHeight - rect.top) * 100) / (rect.height + viewportHeight);
      }
  
      progress = Math.min(Math.max(progress, 0), 100);
      callback(progress, el);
    });
  
    ticking = false;
  };
  
  const onScrollOrResize = (e) => {
    if (e.type === 'resize') needsHeightSync = true;
    if (!ticking) {
      requestAnimationFrame(updateScrollProgress);
      ticking = true;
    }
  };

  window.addEventListener('scroll', onScrollOrResize, { passive: true });
  window.addEventListener('resize', onScrollOrResize, { passive: true });

  const observeScrollProgress = (el, callback, mode = 'full') => {
    const item = { el, callback, mode };
    progressElements.add(item);
    requestAnimationFrame(updateScrollProgress);
    return () => progressElements.delete(item);
  };

  /* ---------------- PARALLAX HANDLER ---------------- */

  const parallaxHeightPairs = [];
  
  const handleParallax = id => {
    const container = document.getElementById(id);
    if (!container) return;
  
    const start = parseFloat(container.dataset.jsParallaxStart) || 0;
    const end = parseFloat(container.dataset.jsParallaxEnd) || 0;
    const unit = container.dataset.jsParallaxUnit || 'px';
    const property = container.dataset.jsParallaxProperty || 'transform'; 
    const mode = container.dataset.jsParallaxMode || 'full';
    const targets = container.querySelectorAll('[data-js-parallax]');

    let scrollRef = container;

    targets.forEach(t => {
      const ref = t.closest('[data-js-parallax-height-reference]')
        || container.querySelector('[data-js-parallax-height-reference]');
      if (ref) {
        t.style.height = `${ref.offsetHeight}px`;
        parallaxHeightPairs.push({ target: t, ref });
        if (ref.hasAttribute('data-js-parallax-scroll-reference')) {
          scrollRef = ref;
        }
      }
    });
  
    observeScrollProgress(scrollRef, (progress) => {
      const value = start + (end - start) * (progress / 100);
  
      targets.forEach(t => {
        if (property === 'transform') {
          t.style.transform = `translateY(${value}${unit})`;
        } else if (property === 'backgroundSize') {
          t.style.backgroundSize = `${value}${unit} 100%`;
        } else {
          t.style[property] = `${value}${unit}`;
        }
      });
    }, mode);
  };

  /* ---------------- EVENT LISTENERS ---------------- */

  const eventDispatcher = (eventType, detail) => {
    document.dispatchEvent(new CustomEvent(eventType, {
      bubbles: true,
      detail: detail
    }));
  };

  return { handleImageLoad, observeImages, setImagesize, resizeObserver, formatMoney, observeScrollProgress, handleParallax, eventDispatcher };

})();


/* ---------------- ASSET LOADER ----------------
   Owned by the producer of window.KS, not by this file: the app embed
   (_KS/_dev/_extension/ks-global-settings.liquid) in the app, head-variables.liquid on
   the skeleton target. Both define KS.assets / KS.styles / loadScript(s) / loadStyle(s) /
   loadSectionAssets synchronously in <head>, before any section's inline
   `KS.loadScripts(...)` runs. This file only has to signal that the runtime below exists
   (see the KS.flush() call at the end). */

/* ---------------- PRODUCT FORM HELPER (for app blocks) ---------------- */

window.KS.productForm = (() => {

  const findForm = (blockEl) => {
    // 1. Inside a form (some themes wrap everything)
    const parentForm = blockEl.closest('form[action*="/cart/add"]');
    if (parentForm) return parentForm;
    // 2. Within the same section
    const section = blockEl.closest('[id^="shopify-section-"]') || blockEl.closest('section');
    if (section) {
      const sectionForm = section.querySelector('form[action*="/cart/add"]');
      if (sectionForm) return sectionForm;
    }
    // 3. Fallback — first product form on page
    return document.querySelector('form[action*="/cart/add"]');
  };

  const getVariantId = (form) => {
    const input = form?.querySelector('input[name="id"]');
    return input ? input.value : null;
  };

  const onVariantChange = (form, callback) => {
    const input = form?.querySelector('input[name="id"]');
    if (!input) return null;
    const observer = new MutationObserver(() => callback(input.value));
    observer.observe(input, { attributes: true, attributeFilter: ['value'] });
    return observer;
  };

  const bindFields = (blockEl, form) => {
    if (!form) return;
    // form.id can be shadowed by <input name="id"> inside the form,
    // so use getAttribute to get the actual form id attribute
    let formId = form.getAttribute('id');
    if (!formId) {
      formId = `ks-product-form-${Date.now()}`;
      form.setAttribute('id', formId);
    }
    blockEl.querySelectorAll('input, select, textarea').forEach(el => {
      el.setAttribute('form', formId);
    });
  };

  return { findForm, getVariantId, onVariantChange, bindFields };

})();

/* Auto-detect quick buy on page and load its scripts. Its CSS is compiled into the
   theme-linked ks-component-product-card.css, so there is no stylesheet to request.
   The normal path is the card's own data-load-script rows, read by the section wrapper's
   loadSectionAssets() (section.liquid) — this is the fallback for a host that renders a card
   outside that wrapper. Same names as the rows: quick add-to-cart is a product form
   (component-product-form) and quick view opens a modal (component-modal). The `ks-` tags
   are the source names; the build renames them to the payload prefix. Safe to request
   twice: the registry dedupes by name. Runs at DOMContentLoaded, after the embed defined
   the loader in <head> and this deferred file ran. */
document.addEventListener('DOMContentLoaded', () => {
  if (document.querySelector('ks-quick-view-product, ks-quick-add-to-cart')) {
    window.KS.loadScripts('component-quick-buy,component-product-form,component-modal');
  }
});

/* Hand the queued component scripts to the loader now that KS.functions / KS.keycodes
   exist. The embed defines flush(); head-variables (skeleton target) injects eagerly and
   has no queue, hence the guard. */
if (typeof window.KS.flush === 'function') {
  window.KS.flush();
} else {
  window.KS.ready = true;
}